import { ADDRESS_LOOKUP_LANGUAGE } from "./address-lookup-language";
import type { IsoCountryCode } from "@/lib/countries";
import { countryCodeForValue } from "@/lib/countries";

export interface AddressComponents {
  streetNumber?: string;
  streetName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  province: string;
  country: IsoCountryCode | "";
  postalCode?: string;
}

export interface GeocodeResult {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  isApproximate: boolean; // true if not ROOFTOP precision
  addressComponents?: AddressComponents;
}

function parseAddressComponents(
  result: google.maps.GeocoderResult
): AddressComponents | undefined {
  const components = result.address_components;
  if (!components) return undefined;

  const getComponent = (type: string): string | undefined => {
    const component = components.find((c) => c.types.includes(type));
    return component?.long_name;
  };

  const getShortComponent = (type: string): string | undefined => {
    const component = components.find((c) => c.types.includes(type));
    return component?.short_name;
  };

  const streetNumber = getComponent("street_number");
  const streetName = getComponent("route");
  const subpremise = getComponent("subpremise");
  const city =
    getComponent("locality") ??
    getComponent("sublocality") ??
    getComponent("administrative_area_level_3") ??
    "";
  const province = getShortComponent("administrative_area_level_1") ?? "";
  const country = countryCodeForValue(getShortComponent("country") ?? "") ?? "";
  const postalCode = getComponent("postal_code");

  // Build address line 1
  let addressLine1 = "";
  if (streetNumber && streetName) {
    addressLine1 = `${streetNumber} ${streetName}`;
  } else if (streetName) {
    addressLine1 = streetName;
  }

  return {
    streetNumber,
    streetName,
    addressLine1,
    addressLine2: subpremise,
    city,
    province,
    country,
    postalCode,
  };
}

export async function geocodeAddress(address: {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country?: string;
}): Promise<GeocodeResult | null> {
  // Construct the address string
  const addressParts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.postalCode,
    address.country ?? "Canada",
  ].filter(Boolean);

  const addressString = addressParts.join(", ");

  try {
    // Use Google Maps Geocoding API
    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      void geocoder.geocode(
        { address: addressString, language: ADDRESS_LOOKUP_LANGUAGE },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
            const result = results[0];
            // ROOFTOP = precise, anything else is approximate
            const locationType = result.geometry.location_type;
            const isApproximate =
              locationType !== google.maps.GeocoderLocationType.ROOFTOP;
            resolve({
              latitude: result.geometry.location.lat(),
              longitude: result.geometry.location.lng(),
              formattedAddress: result.formatted_address,
              isApproximate,
              addressComponents: parseAddressComponents(result),
            });
          } else {
            console.warn(
              "Geocoding failed for address:",
              addressString,
              status
            );
            resolve(null);
          }
        }
      );
    });
  } catch (error) {
    console.error("Error geocoding address:", error);
    return null;
  }
}
